/**
 * Tenant Service — Frontend client for the multi-tenant management API.
 *
 * Provides typed methods for:
 * - Creating and managing tenants
 * - Managing tenant knowledge base
 * - Managing tenant users and roles
 * - Resolving effective Prest0n config for the current user
 */

import { getAuth } from 'firebase/auth';

const API_BASE = process.env.REACT_APP_FUNCTIONS_URL || 'https://us-central1-tensile-tenure-453409-k3.cloudfunctions.net';
const TENANT_ENDPOINT = `${API_BASE}/tenantApi`;

// ============================================================================
// Types
// ============================================================================

export interface TenantProfile {
  firmName: string;
  brandTone?: string;
  website?: string;
  domain?: string;
  practiceAreas?: string[];
  attorneys?: Array<{ name: string; title: string; email?: string }>;
  contactInfo?: { phone?: string; email?: string; address?: string };
  timezone?: string;
  defaultLanguage?: string;
  logoUrl?: string;
}

export interface TenantRuntimeConfig {
  allowedWorkflows?: string[];
  enabledCapabilities?: string[];
  defaultLanguage?: string;
  escalationRules?: string;
  disclaimers?: string;
  maxUsersPerFirm?: number;
  chatWelcomeMessage?: string;
  personaNameOverride?: string;
}

export interface KnowledgeDoc {
  title: string;
  category: string;
  content: string;
  sections?: Array<{ heading: string; content: string }>;
  language?: string;
  isPublic?: boolean;
}

export interface EffectiveConfig {
  guardrails: any;
  identity: { name: string; emoji: string; version: string };
  soul: any;
  behavior: any;
  workflows: Array<{ id: string; name: string; description: string; [key: string]: any }>;
  capabilities: Array<{ id: string; name: string; description: string; defaultEnabled: boolean }>;
  templates: Array<{ id: string; name: string; type: string }>;
  tenant: {
    id: string;
    profile: TenantProfile;
    config: TenantRuntimeConfig;
    knowledge: Array<{ id: string } & KnowledgeDoc>;
    prompts: any[];
    integrations: any[];
  } | null;
  user: {
    profile: any;
    settings: any;
    integrations: any[];
    role: 'admin' | 'staff' | 'viewer';
    permissions: string[];
  };
}

// ============================================================================
// Auth Helper
// ============================================================================

async function getIdToken(): Promise<string> {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  return user.getIdToken();
}

async function tenantRequest<T>(body: Record<string, any>): Promise<T> {
  const token = await getIdToken();
  const response = await fetch(TENANT_ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(errorData.error || `HTTP ${response.status}`);
  }

  return response.json();
}

// ============================================================================
// Tenant CRUD
// ============================================================================

/**
 * Create a new tenant. The caller becomes the tenant admin.
 */
export async function createTenant(
  firmName: string,
  profile?: Partial<TenantProfile>,
  config?: Partial<TenantRuntimeConfig>,
): Promise<{ tenantId: string }> {
  return tenantRequest({
    action: 'createTenant',
    firmName,
    profile,
    config,
  });
}

/**
 * Update the tenant profile (firm info, branding, etc.)
 */
export async function updateTenantProfile(
  tenantId: string,
  updates: Partial<TenantProfile>,
): Promise<void> {
  await tenantRequest({
    action: 'updateTenantProfile',
    tenantId,
    updates,
  });
}

/**
 * Update the tenant runtime config (allowed workflows, capabilities, etc.)
 */
export async function updateTenantConfig(
  tenantId: string,
  updates: Partial<TenantRuntimeConfig>,
): Promise<void> {
  await tenantRequest({
    action: 'updateTenantConfig',
    tenantId,
    updates,
  });
}

// ============================================================================
// Knowledge Base
// ============================================================================

/**
 * Add a knowledge document to a tenant.
 */
export async function addKnowledgeDoc(
  tenantId: string,
  doc: KnowledgeDoc,
): Promise<{ docId: string }> {
  return tenantRequest({
    action: 'addKnowledge',
    tenantId,
    doc,
  });
}

/**
 * Update an existing knowledge document.
 */
export async function updateKnowledgeDoc(
  tenantId: string,
  docId: string,
  updates: Partial<KnowledgeDoc>,
): Promise<void> {
  await tenantRequest({
    action: 'updateKnowledge',
    tenantId,
    docId,
    updates,
  });
}

/**
 * Delete a knowledge document.
 */
export async function deleteKnowledgeDoc(
  tenantId: string,
  docId: string,
): Promise<void> {
  await tenantRequest({
    action: 'deleteKnowledge',
    tenantId,
    docId,
  });
}

// ============================================================================
// User Management
// ============================================================================

/**
 * Invite a user to a tenant with a specific role.
 */
export async function inviteUser(
  tenantId: string,
  targetUid: string,
  role: 'admin' | 'staff' | 'viewer' = 'staff',
): Promise<void> {
  await tenantRequest({
    action: 'inviteUser',
    tenantId,
    targetUid,
    role,
  });
}

/**
 * Remove a user from a tenant (sets status to suspended).
 */
export async function removeUser(
  tenantId: string,
  targetUid: string,
): Promise<void> {
  await tenantRequest({
    action: 'removeUser',
    tenantId,
    targetUid,
  });
}

/**
 * Update a user's role within a tenant.
 */
export async function updateUserRole(
  tenantId: string,
  targetUid: string,
  newRole: 'admin' | 'staff' | 'viewer',
): Promise<void> {
  await tenantRequest({
    action: 'updateUserRole',
    tenantId,
    targetUid,
    newRole,
  });
}

// ============================================================================
// Config Resolution
// ============================================================================

/**
 * Resolve the effective Prest0n config for the current user.
 * Merges global → tenant → user layers.
 */
export async function resolveEffectiveConfig(): Promise<EffectiveConfig> {
  const result = await tenantRequest<{ config: EffectiveConfig }>({
    action: 'resolveConfig',
  });
  return result.config;
}

// ============================================================================
// Admin
// ============================================================================

/**
 * Seed global collections (admin-only).
 */
export async function seedGlobalCollections(): Promise<{
  seeded: string[];
  skipped: string[];
  errors: string[];
}> {
  return tenantRequest({
    action: 'seedGlobal',
  });
}
